import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  History, Plus, Edit, Trash2, UserPlus, UserMinus, Clock, User, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ConfigHistoryPanel({ configType = 'kpi', configId = null }) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [configType, configId]);

  const loadHistory = async () => {
    try {
      let url = `${API_URL}/api/config/history?config_type=${configType}`;
      if (configId) {
        url += `&config_id=${configId}`;
      }
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'created': return <Plus className="h-4 w-4 text-green-500" />;
      case 'updated': return <Edit className="h-4 w-4 text-blue-500" />;
      case 'deleted': return <Trash2 className="h-4 w-4 text-red-500" />;
      case 'assignment_added': return <UserPlus className="h-4 w-4 text-purple-500" />;
      case 'assignment_removed': return <UserMinus className="h-4 w-4 text-orange-500" />;
      default: return <Settings className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'created': return 'bg-green-50 border-green-200';
      case 'updated': return 'bg-blue-50 border-blue-200';
      case 'deleted': return 'bg-red-50 border-red-200';
      case 'assignment_added': return 'bg-purple-50 border-purple-200';
      case 'assignment_removed': return 'bg-orange-50 border-orange-200';
      default: return 'bg-muted/50 border-border';
    }
  };

  const formatChangeType = (type) => {
    switch (type) {
      case 'created': return 'Created';
      case 'updated': return 'Updated';
      case 'deleted': return 'Deleted';
      case 'assignment_added': return 'Assignment Added';
      case 'assignment_removed': return 'Assignment Removed';
      default: return type;
    }
  };

  const renderChanges = (changes, previousValues, newValues) => {
    if (!changes || Object.keys(changes).length === 0) {
      // Try to show diff between previous and new values
      if (previousValues && newValues) {
        const changedKeys = Object.keys(newValues).filter(key => 
          JSON.stringify(previousValues[key]) !== JSON.stringify(newValues[key])
        );
        if (changedKeys.length > 0) {
          return (
            <div className="text-xs space-y-1 mt-2">
              {changedKeys.slice(0, 5).map(key => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                  <span className="text-red-500 line-through">{String(previousValues[key])}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-green-600 font-medium">{String(newValues[key])}</span>
                </div>
              ))}
              {changedKeys.length > 5 && (
                <div className="text-muted-foreground">+{changedKeys.length - 5} more changes</div>
              )}
            </div>
          );
        }
      }
      return null;
    }

    return (
      <div className="text-xs space-y-1 mt-2">
        {Object.entries(changes).slice(0, 5).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
            <span className="text-foreground font-medium">{String(value)}</span>
          </div>
        ))}
        {Object.keys(changes).length > 5 && (
          <div className="text-muted-foreground">+{Object.keys(changes).length - 5} more changes</div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5 text-indigo-500" />
          Configuration Change History
        </CardTitle>
        <CardDescription>
          Track all changes made to {configType.toUpperCase()} configurations for transparency
        </CardDescription>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No configuration changes recorded yet</p>
            <p className="text-sm mt-1">Changes will appear here when configurations are modified</p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-muted" />
              
              <div className="space-y-4">
                {history.map((item, index) => (
                  <div key={item.id} className="relative pl-10">
                    {/* Timeline dot */}
                    <div className={`absolute left-2 top-3 w-4 h-4 rounded-full border-2 bg-card flex items-center justify-center ${getChangeColor(item.change_type)}`}>
                      {getChangeIcon(item.change_type)}
                    </div>
                    
                    <div className={`p-4 rounded-lg border ${getChangeColor(item.change_type)}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize">
                              {formatChangeType(item.change_type)}
                            </Badge>
                            <span className="font-medium text-foreground">
                              {item.config_name}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {item.changed_by_name || 'System'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(item.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {renderChanges(item.changes, item.previous_values, item.new_values)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
